/**
 * "Members should never pay more" — one rule for the class form, the class
 * create/edit API, and every place a member drop-in price is shown.
 *
 * The discounted drop-in rate is offered only when it can be charged:
 * Open Gym at any schedule count, a normal class only with 2 or more
 * schedules. That is the same gate as the field in ClassPricingStructure
 * and as resolveDropInMemberPricing. A normal class with 0 or 1 schedules
 * hides the field, so a stored rate that is higher than the standard rate
 * must not block Save — staff cannot see or fix it.
 *
 * scheduleCount null means the caller does not know how many schedules
 * apply. The rate might still be charged, so the check stays on.
 *
 * dropInEnabled false means the class's "Allow Drop-Ins" toggle is off. No
 * drop-in is charged then (the enroll route refuses it), and the form hides
 * both drop-in fields, so the discount is not offered either. Undefined or
 * null means the caller does not know, and counts as on — the column default.
 * Open Gym ignores it: the class save forces drop-ins on for Open Gym after
 * this check runs (OPEN_GYM_FORCED_DEFAULTS), so a payload saying "off" would
 * otherwise skip the check and then be stored as on.
 *
 * A class edit can ask to remove a schedule and still leave it in place
 * when athletes are on it. The form then has one schedule (the check on
 * the payload passes) while the class still has two. memberRateAfterScheduleSync
 * lowers the stored discount to the drop-in rate in that case, after the
 * schedule changes have actually landed. A save that truly leaves fewer
 * than 2 schedules keeps the stored discount.
 */

export const MEMBER_RATE_ABOVE_STANDARD_MESSAGE =
  "The member rate can't be higher than the standard rate — members should never pay more.";

export const MEMBER_RATE_CLAMPED_TO_STANDARD_NOTICE =
  'The discounted drop-in rate was lowered to match the drop-in rate.';

export const DISCOUNTED_DROP_IN_NEEDS_TWO_SCHEDULES =
  'Available once this class has 2 or more schedules.';

export function finitePrice(value: unknown): number | null {
  if (typeof value === 'boolean' || value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function scheduleCountForMemberRateCheck(sections: unknown): number | null {
  return Array.isArray(sections) ? sections.length : null;
}

export function discountedDropInRateIsOffered(input: {
  isOpenGym: boolean;
  scheduleCount: number | null;
  dropInEnabled?: boolean | null;
}): boolean {
  if (input.isOpenGym) return true;
  if (input.dropInEnabled === false) return false;
  if (input.scheduleCount == null) return true;
  return input.scheduleCount >= 2;
}

export function memberRateHigherThanStandardError(input: {
  memberDropInEnabled: boolean;
  memberDropInRate: unknown;
  standardDropInRate: unknown;
  isOpenGym: boolean;
  scheduleCount: number | null;
  dropInEnabled?: boolean | null;
}): string | null {
  if (!input.memberDropInEnabled) return null;
  if (!discountedDropInRateIsOffered({
    isOpenGym: input.isOpenGym,
    scheduleCount: input.scheduleCount,
    dropInEnabled: input.dropInEnabled,
  })) {
    return null;
  }
  const standardNum = finitePrice(input.standardDropInRate);
  const memberNum = finitePrice(input.memberDropInRate);
  if (standardNum == null || memberNum == null) return null;
  if (memberNum > standardNum) return MEMBER_RATE_ABOVE_STANDARD_MESSAGE;
  return null;
}

/**
 * After a class edit's schedule changes have been applied: when this save
 * was allowed to keep a higher member rate only because the form had fewer
 * than 2 schedules, lower that stored rate if 2 or more schedules are still
 * active — or if that count could not be read. A known count under 2 keeps
 * the stored discount. Open Gym and a payload that already showed the field
 * are rejected before this runs, so they are left alone here. Drop-ins off
 * leaves it alone too: the rate is not charged, and lowering it would write
 * a hidden value (possibly $0) that applies once drop-ins are turned on.
 */
export function memberRateAfterScheduleSync(input: {
  isOpenGym: boolean;
  payloadScheduleCount: number | null;
  memberDropInEnabled: boolean;
  memberDropInRate: unknown;
  standardDropInRate: unknown;
  remainingActiveScheduleCount: number | null;
  dropInEnabled?: boolean | null;
}): { clampTo: number | null; notice: string | null } {
  const keep = { clampTo: null, notice: null };
  if (input.isOpenGym) return keep;
  if (input.payloadScheduleCount == null || input.payloadScheduleCount >= 2) return keep;
  const higher = memberRateHigherThanStandardError({
    memberDropInEnabled: input.memberDropInEnabled,
    memberDropInRate: input.memberDropInRate,
    standardDropInRate: input.standardDropInRate,
    isOpenGym: false,
    scheduleCount: 2,
    dropInEnabled: input.dropInEnabled,
  });
  if (!higher) return keep;
  if (input.remainingActiveScheduleCount != null && input.remainingActiveScheduleCount < 2) {
    return keep;
  }
  const standard = finitePrice(input.standardDropInRate);
  if (standard == null) return keep;
  return { clampTo: standard, notice: MEMBER_RATE_CLAMPED_TO_STANDARD_NOTICE };
}

/** Adding one schedule makes the stored member rate chargeable and it is higher. */
export function memberRateErrorWhenAddingSchedule(input: {
  isOpenGym: boolean;
  activeScheduleCountBefore: number;
  memberDropInEnabled: boolean;
  memberDropInRate: unknown;
  standardDropInRate: unknown;
  dropInEnabled?: boolean | null;
}): string | null {
  return memberRateHigherThanStandardError({
    memberDropInEnabled: input.memberDropInEnabled,
    memberDropInRate: input.memberDropInRate,
    standardDropInRate: input.standardDropInRate,
    isOpenGym: input.isOpenGym,
    scheduleCount: input.activeScheduleCountBefore + 1,
    dropInEnabled: input.dropInEnabled,
  });
}

/**
 * The member price to print. Null when the discount is not offered or either
 * price is not a real number. When both are real, the printed price is the
 * lower of the two — a stored member price above the drop-in price is never shown.
 */
export function memberDropInPriceToShow(input: {
  isOpenGym: boolean;
  scheduleCount: number | null;
  memberDropInEnabled: boolean;
  memberDropInRate: unknown;
  standardDropInRate: unknown;
  dropInEnabled?: boolean | null;
}): number | null {
  if (!input.memberDropInEnabled) return null;
  if (!discountedDropInRateIsOffered({
    isOpenGym: input.isOpenGym,
    scheduleCount: input.scheduleCount,
    dropInEnabled: input.dropInEnabled,
  })) {
    return null;
  }
  const member = finitePrice(input.memberDropInRate);
  const standard = finitePrice(input.standardDropInRate);
  if (member == null || standard == null) return null;
  return Math.min(member, standard);
}
